// Ein einzelnes Gespräch: die Karte mit ganzer Notiz und Player, darunter die Leitungsqualität mit Kurve der Sekundenproben und der
// Zeitleiste der Warnungen. Erreichbar aus dem Verlauf (Karte, Menü „Details öffnen“) und aus der Qualitätsübersicht.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { gespraecheApi, type Gespraech, type QualitaetEinzel } from "../api/gespraeche";
import { Laden } from "../components/Bausteine";
import { FehlerBox } from "../components/Fehler";
import { Icon } from "../components/Icon";
import { useSeitenKopf } from "../daten/seite";
import { T } from "../texte";
import { zeitpunktText } from "../werkzeuge/zeit";
import { GespraechKarte } from "./GespraechKarte";
import { GespraecheKopf } from "./GespraecheKopf";
import { BEWERTUNG_TEXT, kurvenPfad, msText, prozentText, zahlText } from "./logik";
import "./gespraeche.css";

function MosKurve({ proben }: { proben: NonNullable<QualitaetEinzel["proben"]> }) {
  const werte = useMemo(() => proben.map((p) => p.mos), [proben]);
  const breite = 600;
  const hoehe = 100;
  // MOS läuft von 1 (unbrauchbar) bis 5 (sehr gut)
  const pfad = kurvenPfad(werte, breite, hoehe, 1, 5);
  const y = (mos: number) => hoehe - ((mos - 1) / 4) * hoehe;
  if (!pfad) return null;
  return (
    <svg className="gesp-kurve" viewBox={`-26 -8 ${breite + 34} ${hoehe + 18}`} preserveAspectRatio="none" role="img" aria-label="Verlauf der Gesprächsqualität (MOS) über das Gespräch">
      {[1, 3, 5].map((m) => (
        <g key={m}>
          <line className="gesp-kurve__raster" x1={0} x2={breite} y1={y(m)} y2={y(m)} />
          <text className="gesp-kurve__beschriftung" x={-6} y={y(m) + 3} textAnchor="end">
            {m}
          </text>
        </g>
      ))}
      <line className="gesp-kurve__schwelle" x1={0} x2={breite} y1={y(3.1)} y2={y(3.1)} />
      <path className="gesp-kurve__linie" d={pfad} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export function GespraechDetail() {
  const { callId = "" } = useParams();
  const navigate = useNavigate();
  const [daten, setDaten] = useState<{ gespraech: Gespraech; qualitaet: QualitaetEinzel | null } | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [nochmal, setNochmal] = useState(0);
  useSeitenKopf([{ label: T.nav.gespraeche, to: "/gespraeche" }, { label: daten?.gespraech.titel ?? "Gespräch" }]);

  useEffect(() => {
    const ctrl = new AbortController();
    setDaten(null);
    setFehler(null);
    gespraecheApi
      .eins(callId, ctrl.signal)
      .then(setDaten)
      .catch((e) => (e as Error).name !== "AbortError" && setFehler(e));
    return () => ctrl.abort();
  }, [callId, nochmal]);

  const q = daten?.qualitaet ?? null;
  return (
    <div className="gesp">
      <GespraecheKopf />
      <div className="gesp-detail">
        <button type="button" className="knopf knopf--leise knopf--klein gesp-detail__zurueck" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/gespraeche"))}>
          <Icon name="pfeil-links" groesse={13} />
          Zurück
        </button>
        {fehler ? <FehlerBox fehler={fehler} onNochmal={() => setNochmal((n) => n + 1)} /> : null}
        {!daten && !fehler ? <Laden /> : null}
        {daten ? (
          <>
            <GespraechKarte gespraech={daten.gespraech} aufgeklappt einzeln />
            <section>
              <h2 className="gesp-abschnitt__titel">Leitungsqualität</h2>
              {q ? (
                <>
                  <dl className="gesp-werte">
                    <div>
                      <dt>Bewertung</dt>
                      <dd>
                        {q.bewertung ? <span className={`gesp-bewertung gesp-bewertung--${q.bewertung}`}>{BEWERTUNG_TEXT[q.bewertung]}</span> : "nicht gemessen"}
                      </dd>
                    </div>
                    <div>
                      <dt>Ø MOS</dt>
                      <dd>{zahlText(q.mos, 1) || "—"}</dd>
                    </div>
                    <div>
                      <dt>Schlechtester MOS</dt>
                      <dd>{zahlText(q.mos_min ?? null, 1) || "—"}</dd>
                    </div>
                    <div>
                      <dt>Paketverlust (Ø)</dt>
                      <dd>{prozentText(q.paketverlust_prozent) || "—"}</dd>
                    </div>
                    <div>
                      <dt>Jitter (Höchstwert)</dt>
                      <dd>{msText(q.jitter_ms) || "—"}</dd>
                    </div>
                    <div>
                      <dt>Laufzeit (Höchstwert)</dt>
                      <dd>{msText(q.rtt_ms) || "—"}</dd>
                    </div>
                    <div>
                      <dt>Neu verbunden</dt>
                      <dd>{q.wiederverbindungen ?? 0}</dd>
                    </div>
                    <div>
                      <dt>Standort / Codec</dt>
                      <dd>{[q.edge, q.codec].filter(Boolean).join(" · ") || "—"}</dd>
                    </div>
                    <div>
                      <dt>Mikrofon</dt>
                      <dd>{q.eingang ?? "—"}</dd>
                    </div>
                  </dl>
                  {q.fehler ? <p className="gesp-erklaerung">Fehler beim Aufbau: {q.fehler}</p> : null}
                  {q.proben?.length ? (
                    <>
                      <h3 className="gesp-abschnitt__titel" style={{ marginTop: "var(--space-4)" }}>
                        Verlauf der Qualität <span className="gesp-abschnitt__unter">MOS über das Gespräch, gestrichelt die Grenze zu „schlecht“ (3,1)</span>
                      </h3>
                      <MosKurve proben={q.proben} />
                    </>
                  ) : null}
                  {q.zeitleiste?.length ? (
                    <>
                      <h3 className="gesp-abschnitt__titel" style={{ marginTop: "var(--space-4)" }}>
                        Warnungen während des Gesprächs
                      </h3>
                      <ul className="gesp-zeitleiste">
                        {q.zeitleiste.map((w, i) => (
                          <li key={`${w.name}-${i}`}>
                            <time>{w.zeit ? zeitpunktText(w.zeit, false).slice(-5) : ""}</time>
                            <span>
                              {w.text} <span className="gesp-hinweis">{w.aktiv ? "begann" : "war wieder weg"}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </>
              ) : (
                <p className="gesp-erklaerung">
                  Für dieses Gespräch gibt es keine Messwerte. Nur Anrufe über die CRM-Telefonie im Browser werden gemessen; Anrufe aus Close und vom Handy nicht.
                </p>
              )}
              {daten.gespraech.lead_id ? (
                <p style={{ marginTop: "var(--space-4)" }}>
                  <Link to={`/lead/${encodeURIComponent(daten.gespraech.lead_id)}`}>Zum Lead {daten.gespraech.lead_name ?? ""}</Link>
                </p>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
}
